import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GearSix, FloppyDisk } from "@phosphor-icons/react";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Failed to load settings"));
  }, []);

  if (!s) return <div className="p-8 data-label text-ink-500">Loading…</div>;

  const update = (patch) => setS((x) => ({ ...x, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="settings-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="data-label text-amber mb-1">Configuration</div>
          <h1 className="font-heading text-4xl tracking-tight font-medium">Settings</h1>
          <p className="text-ink-400 mt-1 text-sm">Shop info, default labor rate, overhead and margin.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="bg-amber hover:bg-amber-hover text-ink-950 font-semibold">
          <FloppyDisk size={16} weight="bold" className="mr-1.5" />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Shop">
            <FieldRow>
              <Field label="Shop name">
                <Input
                  data-testid="shop-name-input"
                  value={s.shop_name} onChange={(e) => update({ shop_name: e.target.value })}
                  className="bg-ink-950 border-ink-700"
                />
              </Field>
              <Field label="Email">
                <Input
                  data-testid="shop-email-input"
                  value={s.shop_email} onChange={(e) => update({ shop_email: e.target.value })}
                  className="bg-ink-950 border-ink-700"
                />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Address">
                <Input
                  data-testid="shop-address-input"
                  value={s.shop_address} onChange={(e) => update({ shop_address: e.target.value })}
                  className="bg-ink-950 border-ink-700"
                />
              </Field>
              <Field label="Phone">
                <Input
                  data-testid="shop-phone-input"
                  value={s.shop_phone} onChange={(e) => update({ shop_phone: e.target.value })}
                  className="bg-ink-950 border-ink-700"
                />
              </Field>
            </FieldRow>
          </Section>

          <Section title="Pricing Defaults">
            <FieldRow>
              <Field label="Labor rate ($/hr)">
                <Input
                  data-testid="labor-rate-input"
                  type="number" step="0.01"
                  value={s.labor_rate} onChange={(e) => update({ labor_rate: parseFloat(e.target.value) || 0 })}
                  className="bg-ink-950 border-ink-700 num text-right"
                />
              </Field>
              <Field label="Overhead (%)">
                <Input
                  data-testid="overhead-pct-input"
                  type="number" step="0.1"
                  value={s.overhead_pct} onChange={(e) => update({ overhead_pct: parseFloat(e.target.value) || 0 })}
                  className="bg-ink-950 border-ink-700 num text-right"
                />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Margin (%)">
                <Input
                  data-testid="margin-pct-input"
                  type="number" step="0.1"
                  value={s.margin_pct} onChange={(e) => update({ margin_pct: parseFloat(e.target.value) || 0 })}
                  className="bg-ink-950 border-ink-700 num text-right"
                />
              </Field>
              <Field label="Sales tax (%)">
                <Input
                  data-testid="tax-pct-input"
                  type="number" step="0.01"
                  value={s.tax_pct} onChange={(e) => update({ tax_pct: parseFloat(e.target.value) || 0 })}
                  className="bg-ink-950 border-ink-700 num text-right"
                />
              </Field>
            </FieldRow>
          </Section>
        </div>

        <aside className="space-y-4">
          <div className="border border-ink-700 bg-ink-900 rounded-md overflow-hidden">
            <div
              className="h-40 bg-cover bg-center"
              style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1631396328075-9c65a7406f09?crop=entropy&cs=srgb&fm=jpg&q=85&w=900)' }}
            />
            <div className="p-5">
              <div className="data-label mb-1 text-amber">Tip</div>
              <p className="text-sm text-ink-400 leading-relaxed">
                Set your true labor rate including burden (taxes, benefits, shop overhead). The AI uses category-based hour estimates that you can fine-tune per line item.
              </p>
            </div>
          </div>
          <div className="border border-ink-700 bg-ink-900 rounded-md p-5">
            <div className="data-label mb-2">How estimates are calculated</div>
            <pre className="text-xs num text-ink-400 leading-relaxed whitespace-pre-wrap">
{`Direct   = Materials + (Labor hrs × $/hr)
Overhead = Direct × Overhead %
Subtotal = (Direct + Overhead) × (1 + Margin %)
Total    = Subtotal × (1 + Tax %)`}
            </pre>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-ink-700 bg-ink-900 rounded-md">
      <div className="px-5 py-3 border-b border-ink-700 flex items-center gap-2">
        <GearSix size={14} weight="duotone" className="text-amber" />
        <div className="data-label">{title}</div>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}
function FieldRow({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="data-label mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
